import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Plus, Trash, Pencil, ArrowRight, Phone, Globe, Users, ChartBar, Download, Upload } from '@phosphor-icons/react';
import { usePagination } from '../components/TablePagination';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUSES = ['New', 'Contacted', 'Qualified', 'Proposal Sent', 'Won', 'Lost'];
const SOURCES = ['Call', 'Landline', 'Website', 'Social', 'Referral', 'Other'];

const STATUS_COLOR = {
  'New': { bg: '#dbeafe', color: '#1e40af', border: '#93c5fd' },
  'Contacted': { bg: '#fef3c7', color: '#92400e', border: '#fcd34d' },
  'Qualified': { bg: '#e0e7ff', color: '#3730a3', border: '#a5b4fc' },
  'Proposal Sent': { bg: '#fce7f3', color: '#9d174d', border: '#f9a8d4' },
  'Won': { bg: '#dcfce7', color: '#166534', border: '#86efac' },
  'Lost': { bg: '#fee2e2', color: '#991b1b', border: '#fca5a5' },
};

const SOURCE_ICON = {
  Call: Phone, Landline: Phone, Website: Globe, Social: Users, Referral: Users, Other: ChartBar,
};

const today = () => new Date().toISOString().split('T')[0];

const blankLead = () => ({
  name: '', company: '', phone: '', email: '',
  source: 'Call', inquiry_date: today(),
  service_interested: '', status: 'New',
  notes: '', assigned_to: '', next_followup: '',
});

const LeadsPage = () => {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('kanban'); // 'kanban' | 'list'
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editing, setEditing] = useState(null); // lead being edited or null
  const [form, setForm] = useState(blankLead());
  const [saving, setSaving] = useState(false);

  const filteredLeads = useMemo(() => {
    const q = search.trim().toLowerCase();
    return leads.filter((l) => {
      if (statusFilter !== 'all' && l.status !== statusFilter) return false;
      if (!q) return true;
      return `${l.name || ''} ${l.company || ''} ${l.phone || ''} ${l.source || ''} ${l.email || ''}`.toLowerCase().includes(q);
    });
  }, [leads, search, statusFilter]);
  const { paged: pagedLeads, controls: leadControls, startIdx: leadStart } = usePagination(filteredLeads, { storageKey: 'leads-page-size', testIdPrefix: 'lead' });

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/leads`, { withCredentials: true });
      setLeads(r.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setForm(blankLead()); setEditing({ id: null }); };
  const startEdit = (lead) => { setForm({ ...blankLead(), ...lead }); setEditing(lead); };
  const cancel = () => { setEditing(null); setForm(blankLead()); };

  const save = async () => {
    if (!form.name.trim()) { alert('Lead name is required'); return; }
    setSaving(true);
    try {
      if (editing?.id) {
        await axios.put(`${API_URL}/api/leads/${editing.id}`, form, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/leads`, form, { withCredentials: true });
      }
      await load();
      cancel();
    } catch (e) {
      alert('Save failed: ' + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this lead?')) return;
    await axios.delete(`${API_URL}/api/leads/${id}`, { withCredentials: true });
    load();
  };

  const setStatus = async (lead, status) => {
    await axios.put(`${API_URL}/api/leads/${lead.id}`, { ...lead, status }, { withCredentials: true });
    load();
  };

  const convert = async (lead) => {
    if (!window.confirm(`Convert "${lead.name}" to a Client?`)) return;
    try {
      const r = await axios.post(`${API_URL}/api/leads/${lead.id}/convert-to-client`, {}, { withCredentials: true });
      alert(`Converted ✓\nClient ID: ${r.data.client_id}\n\nGo to /proposal to draft a proposal.`);
      load();
    } catch (e) { alert('Convert failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const downloadTemplate = () => {
    const header = 'name,company,phone,email,source,inquiry_date,service_interested,status,notes,assigned_to,next_followup';
    const sample = 'Mr. Sample,SampleCo,9876543210,sample@example.com,Call,2026-02-20,Website Development,New,Initial inquiry,Sajjath,2026-02-25';
    const csv = `${header}\n${sample}\n`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'leads_import_template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const importCsv = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await axios.post(`${API_URL}/api/leads/bulk-import`, fd, { withCredentials: true });
      alert(`${r.data.message}${r.data.errors?.length ? '\n\nFirst errors:\n' + r.data.errors.join('\n') : ''}`);
      load();
    } catch (e) { alert('Import failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const byStatus = useMemo(() => {
    const map = {};
    STATUSES.forEach((s) => { map[s] = []; });
    leads.forEach((l) => { (map[l.status] || (map['New'])).push(l); });
    return map;
  }, [leads]);

  return (
    <div data-testid="leads-page" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ color: 'var(--dx-text)' }}>Leads CRM</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--dx-text2)' }}>
            {leads.length} lead{leads.length !== 1 ? 's' : ''} •
            Sources: Call · Landline · Website · Social · Referral
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="download-template-btn" title="Download CSV template">
            <Download size={14} className="mr-1.5" /> Template
          </Button>
          <label className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border rounded-md cursor-pointer hover:bg-gray-50"
            style={{ borderColor: 'var(--dx-border2)', height: '32px' }} data-testid="import-csv-label">
            <Upload size={14} /> Import CSV
            <input type="file" accept=".csv" className="hidden" data-testid="import-csv-input"
              onChange={(e) => { importCsv(e.target.files?.[0]); e.target.value = ''; }} />
          </label>
          <div className="inline-flex border rounded-md overflow-hidden" style={{ borderColor: 'var(--dx-border2)' }}>
            <button onClick={() => setView('kanban')} data-testid="view-kanban-btn"
              className="px-3 py-1.5 text-xs font-semibold"
              style={{ background: view === 'kanban' ? 'var(--dx-primary, #1D9E75)' : 'white', color: view === 'kanban' ? 'white' : 'var(--dx-text)' }}>
              Kanban
            </button>
            <button onClick={() => setView('list')} data-testid="view-list-btn"
              className="px-3 py-1.5 text-xs font-semibold"
              style={{ background: view === 'list' ? 'var(--dx-primary, #1D9E75)' : 'white', color: view === 'list' ? 'white' : 'var(--dx-text)' }}>
              List
            </button>
          </div>
          <Button onClick={startNew} data-testid="new-lead-btn">
            <Plus size={14} className="mr-1.5" /> New Lead
          </Button>
        </div>
      </div>

      {loading ? (
        <Card className="p-8 text-center text-sm" style={{ color: 'var(--dx-text3)' }}>Loading leads…</Card>
      ) : view === 'kanban' ? (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {STATUSES.map((s) => {
            const sc = STATUS_COLOR[s];
            const items = byStatus[s] || [];
            return (
              <Card key={s} className="p-2 min-h-[180px]" data-testid={`column-${s.replace(/\s+/g, '-').toLowerCase()}`}>
                <div className="flex items-center justify-between px-2 py-1 rounded mb-2" style={{ background: sc.bg, color: sc.color }}>
                  <span className="text-xs font-bold uppercase tracking-wide">{s}</span>
                  <span className="text-xs font-semibold">{items.length}</span>
                </div>
                <div className="space-y-2 max-h-[68vh] overflow-y-auto pr-1">
                  {items.map((l) => {
                    const SrcIcon = SOURCE_ICON[l.source] || ChartBar;
                    return (
                      <div key={l.id} className="bg-white border rounded-md p-2 hover:shadow-sm cursor-pointer transition-shadow"
                        style={{ borderColor: sc.border }} onClick={() => startEdit(l)} data-testid={`lead-card-${l.id}`}>
                        <div className="flex items-start justify-between gap-1">
                          <strong className="text-[13px] leading-tight" style={{ color: 'var(--dx-text)' }}>{l.name}</strong>
                          <SrcIcon size={12} style={{ color: 'var(--dx-text3)', flexShrink: 0, marginTop: 2 }} />
                        </div>
                        {l.company && <div className="text-[11px] mt-0.5" style={{ color: 'var(--dx-text2)' }}>{l.company}</div>}
                        {l.phone && <div className="text-[10px] mt-1" style={{ color: 'var(--dx-text3)' }}>📱 {l.phone}</div>}
                        {l.next_followup && <div className="text-[10px] mt-0.5" style={{ color: '#0ea5e9' }}>↪ {l.next_followup}</div>}
                      </div>
                    );
                  })}
                  {items.length === 0 && <div className="text-[10px] text-center py-3" style={{ color: 'var(--dx-text3)' }}>—</div>}
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="p-0 overflow-x-auto">
          <div className="p-3 border-b flex items-center gap-3 flex-wrap" style={{ borderColor: 'var(--dx-border)' }}>
            <Input
              placeholder="Search by name, company, phone, source…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 max-w-md"
              data-testid="lead-search"
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="text-sm px-2 py-2 border rounded bg-white"
              data-testid="lead-status-filter"
            >
              <option value="all">All Status</option>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <table className="w-full text-sm">
            <thead style={{ background: 'var(--dx-bg)' }}>
              <tr>
                <th className="text-left px-3 py-2.5 font-semibold">#</th>
                <th className="text-left px-3 py-2.5 font-semibold">Name</th>
                <th className="text-left px-3 py-2.5 font-semibold">Company</th>
                <th className="text-left px-3 py-2.5 font-semibold">Phone</th>
                <th className="text-left px-3 py-2.5 font-semibold">Source</th>
                <th className="text-left px-3 py-2.5 font-semibold">Status</th>
                <th className="text-left px-3 py-2.5 font-semibold">Next Follow-up</th>
                <th className="text-right px-3 py-2.5 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagedLeads.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-8 text-[#52525B]">{leads.length === 0 ? 'No leads yet — click "New Lead" to add one.' : 'No matches.'}</td></tr>
              ) : pagedLeads.map((l, idx) => {
                const sc = STATUS_COLOR[l.status] || STATUS_COLOR.New;
                return (
                  <tr key={l.id} className="border-t hover:bg-gray-50" data-testid={`lead-row-${l.id}`}>
                    <td className="px-3 py-2 text-gray-400">{leadStart + idx + 1}</td>
                    <td className="px-3 py-2 font-medium">{l.name}</td>
                    <td className="px-3 py-2 text-[#52525B]">{l.company || '—'}</td>
                    <td className="px-3 py-2 text-[#52525B]">{l.phone || '—'}</td>
                    <td className="px-3 py-2"><span className="text-[11px] font-medium">{l.source}</span></td>
                    <td className="px-3 py-2">
                      <select value={l.status} onChange={(e) => setStatus(l, e.target.value)}
                        className="text-[11px] font-semibold border rounded px-1.5 py-0.5"
                        style={{ background: sc.bg, color: sc.color, borderColor: sc.border }}
                        data-testid={`status-select-${l.id}`}>
                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2 text-[11px]" style={{ color: l.next_followup ? '#0ea5e9' : '#9ca3af' }}>{l.next_followup || '—'}</td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        {!l.converted_client_id && (
                          <Button size="sm" variant="outline" onClick={() => convert(l)} data-testid={`convert-${l.id}`} title="Convert to Client">
                            <ArrowRight size={14} />
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => startEdit(l)} data-testid={`edit-${l.id}`}>
                          <Pencil size={14} />
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600" onClick={() => remove(l.id)} data-testid={`delete-${l.id}`}>
                          <Trash size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {leadControls}
        </Card>
      )}

      {/* Edit Modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={cancel}>
          <Card className="w-full max-w-2xl bg-white p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">{editing.id ? 'Edit Lead' : 'New Lead'}</h2>
              <button onClick={cancel} className="text-2xl leading-none px-2">×</button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="lead-name-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Company</Label>
                <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="lead-company-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="lead-phone-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="lead-email-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Source</Label>
                <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}
                  className="w-full border rounded px-3 py-2 text-sm bg-white" data-testid="lead-source-select">
                  {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Inquiry Date</Label>
                <Input type="date" value={form.inquiry_date} onChange={(e) => setForm({ ...form, inquiry_date: e.target.value })} data-testid="lead-inquiry-date" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Status</Label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="w-full border rounded px-3 py-2 text-sm bg-white" data-testid="lead-status-select">
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Service Interested In</Label>
                <Input value={form.service_interested} onChange={(e) => setForm({ ...form, service_interested: e.target.value })}
                  placeholder="e.g. Website Development, Mobile App, Branding" data-testid="lead-service-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Assigned To</Label>
                <Input value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} placeholder={user?.name || ''} data-testid="lead-assigned-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Next Follow-up</Label>
                <Input type="date" value={form.next_followup} onChange={(e) => setForm({ ...form, next_followup: e.target.value })} data-testid="lead-followup-input" />
              </div>
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Notes</Label>
                <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full border rounded px-3 py-2 text-sm min-h-[80px]" placeholder="Call summary, requirements, budget…" data-testid="lead-notes-input" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={cancel} data-testid="lead-cancel-btn">Cancel</Button>
              <Button onClick={save} disabled={saving} data-testid="lead-save-btn">
                {saving ? 'Saving…' : (editing.id ? 'Update' : 'Create')}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default LeadsPage;
